import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-all duration-300 ease-out focus:outline-none focus:ring-0 focus:ring-transparent focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 hover:scale-102 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      {
      variants: {
        variant: {
          default: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-full",
          destructive:
            "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full",
          outline:
            "border border-input bg-white text-foreground hover:bg-accent hover:text-accent-foreground rounded-full",
          secondary:
            "bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-full",
          ghost: "hover:bg-accent hover:text-accent-foreground rounded-full",
          link: "text-primary underline-offset-4 hover:underline rounded-full",
        },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-full px-4",
        lg: "h-11 rounded-full px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
